import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

type Session = {
    id: string | null,
    token: string | null,
};

const settings = {
    session: null as Session | null,
    errorHandler: null as ((e: Error) => void) | null,
};

export function setApiSession({ id = null, token = null }: Partial<Session>) {
    settings.session = { id, token };
}

export function getApiSession() {
    return settings.session;
}


export function setApiErrorHandler(errorHandler: ((error: unknown) => Promise<void>) | null = null) {
    settings.errorHandler = errorHandler;
}



async function requestInterceptor(config: AxiosRequestConfig) {
    const session = getApiSession();
    const endpoint = process.env.VUE_APP_API_URL;

    if (!endpoint) throw new Error('Missing API endpoint');
    config.baseURL = `${endpoint}`;

    if (!session?.id || !session?.token) {
        return config;
    }

    const method = config.method?.toUpperCase();
    if (!method) throw new Error('Undefined request method');

    const url = new URL(`${config.baseURL}/${config.url}`);
    const path = url.pathname;

    // Handle query parameters passed using params instead of hardcoded in URL.
    Object.entries(config.params || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((v) => {
                url.searchParams.append(`${key}[]`, v);
            });
        } else {
            url.searchParams.append(key, value as string);
        }
    });
    // Remove leading question mark,
    // force decode because server doesn't just use whatever the client sends...
    // replace + with space because decodeURIComponent does not do this...
    let body = config.data || '';
    if (body.constructor === FormData) {
        const hashes = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of body) {
            if (value.constructor === File) {
                const hashRef = `__${key}_sha256`;
                hashes.push(body.get(hashRef));
                body.delete(hashRef);
            }
        }
        hashes.sort();
        body = hashes.join('&');
    } else if (typeof (body) !== 'string') {
        body = JSON.stringify(config.data);
    }
    if (config && config.headers) {
        config.headers.Authorization = `Bearer ${session.token}`;
    }

    return config;
}

function makeClient() {
    const client = axios.create({
        baseURL: '',
        headers: {
            common: {
                Accept: 'application/json',
            },
        }
    });
    client.interceptors.request.use(requestInterceptor);
    client.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: AxiosError) => {
            if (settings.errorHandler) {
                return settings.errorHandler(error);
            }
            return Promise.reject(error);
        },
    );
    return client;
}

export const api = makeClient();
